import React, { Dispatch, SetStateAction } from "react";
import TextInput from "../../../components/form-fields/TextInput";
import { FormGroup } from "../../../components/forms";
import { Director, ContractorType } from "../type";
import LimitAlert from "./LimitAlert";

interface IProps {
  ele: Director;
  keyIndex: number;
  originalInput: Director[];
  setState: Dispatch<SetStateAction<ContractorType>>;
  fieldToReplicate: keyof ContractorType;
  formName: string;
  addBtnText: string;
  areOptional: boolean;
  labelPrefix: string;
  maxCount: number;
  limitMessage: string; // Add this prop for the limit message
}

const getLabelName = (areOptional: boolean, keyIndex: number, labelPrefix: string, originalInput: Director[], labelSuffix: string) =>
  `${labelPrefix} ${originalInput.length === 1 ? "" : keyIndex + 1} ${labelSuffix}  ${areOptional ? "(Optional)" : ""}`;

const getTitle = (formName: string, originalInput: Director[], keyIndex: number) => `${formName} ${originalInput.length === 1 ? "" : keyIndex + 1}`;

const renderDeleteButton = (originalInput: Director[], deleteInput: () => void) => {
  if (originalInput.length > 1) {
    return (
      <button
        type={"button"}
        onClick={deleteInput}
        className="icon-middle icon-left-0 icon-5 icon-top-[1.25rem] icon-minus icon-red right text-red h-15 md:h-14 font-bold tex-xl md:w-auto self-end border-none px-8 py-2"
      >
        Delete
      </button>
    );
  }
  return null;
};

const renderAddButton = (
  originalInput: Director[],
  keyIndex: number,
  addInput: () => void,
  addBtnText: string,
  maxCount: number,
  limitMessage: string
) => {
  if (originalInput.length !== maxCount) {
    return (
      <button
        type={"button"}
        onClick={addInput}
        className="flex gap-4 items-center text-red h-15 md:h-14 font-bold tex-xl md:w-auto self-end px-8 py-2 border-2 border-red rounded-full"
      >
        <span className="icon-middle icon-left-0 icon-5 icon-plus icon-red"></span>
        <span>{addBtnText}</span>
      </button>
    );
  }
  return <LimitAlert message={limitMessage} />;
};

const MultiInputField: React.FC<IProps> = ({
  formName,
  setState,
  keyIndex,
  ele,
  originalInput,
  fieldToReplicate,
  addBtnText,
  areOptional,
  labelPrefix,
  maxCount,
  limitMessage,
  ...props
}) => {
  const multiInputChange = (key: string, value: string) =>
    setState(prevState => ({
      ...prevState,
      [fieldToReplicate]: (prevState[fieldToReplicate] as Director[]).map((field, i) => (i === keyIndex ? { ...field, [key]: value } : field)),
    }));

  const deleteInput = () =>
    setState(prevState => ({ ...prevState, [fieldToReplicate]: (prevState[fieldToReplicate] as Director[]).filter((_, i) => i !== keyIndex) }));

  const addInput = () =>
    setState(prevState => ({ ...prevState, [fieldToReplicate]: [...(prevState[fieldToReplicate] as Director[]), { name: "", NRIC: "" }] }));

  return (
    <>
      <div className="flex flex-col">
        <FormGroup title={getTitle(formName, originalInput, keyIndex)} className="flex flex-col">
          <TextInput
            aria-label="ele-name"
            value={ele.name}
            onChange={e => multiInputChange("name", e.target.value)}
            label={getLabelName(areOptional, keyIndex, labelPrefix, originalInput, "Name")}
            disabled={false}
            {...(props as any)[keyIndex].name}
          />
          <TextInput
            value={ele.NRIC}
            aria-label="ele-NRIC"
            onChange={e => multiInputChange("NRIC", e.target.value)}
            label={getLabelName(areOptional, keyIndex, labelPrefix, originalInput, "NRIC")}
            disabled={false}
            {...(props as any)[keyIndex].NRIC}
          />
        </FormGroup>
        {renderDeleteButton(originalInput, deleteInput)}
      </div>
      {originalInput.length - 1 === keyIndex && renderAddButton(originalInput, keyIndex, addInput, addBtnText, maxCount, limitMessage)}
    </>
  );
};

export default MultiInputField;
